/**
 * @module app.FeatureConverter
 */
import {calcDistanceForResolution, convertColorToCesium, olGeometryCloneTo4326, ol4326CoordinateArrayToCsCartesians} from 'ol-cesium/src/olcs/core.ts';
import olcsFeatureConverter from 'ol-cesium/src/olcs/FeatureConverter.js';
import {get as olProjTransformsGet} from 'ol/proj/transforms';
import {mapProjectionExtentToLonLat, QUERY_HIGHLIGHT_ID} from './utils';
import {proj as proj21781} from '@geoblocks/proj/src/EPSG_21781.js';
import {ARROW_3D_INTERVAL_METERS} from "./arrowstyle";


/**
 * @private
 * @param {!Array.<!Array.<number>>} polygonLatLng
 * @param {!Cesium.Color} cesiumColor
 * @param {number} level
 * @return {!Cesium.GroundPrimitive}
 * Create a GroundPrimitive from polygon coordinates that is visible at a given zoom level.
 * The polygon is filled with the given color.
 */
export function makePolygonPrimitive_(polygonLatLng, cesiumColor, level) {
  const positions = polygonLatLng.map(coordinates => Cesium.Cartesian3.fromDegrees(coordinates[0], coordinates[1]));
  const polygonHierarchy = new Cesium.PolygonHierarchy(positions);
  const geometry = new Cesium.PolygonGeometry({polygonHierarchy});

  const color = Cesium.ColorGeometryInstanceAttribute.fromColor(cesiumColor);

  const distanceFactor = 2000;
  const near = (level === 0) ? 0 : distanceFactor * Math.pow(2, level - 1);
  const far = distanceFactor * Math.pow(2, level);
  const distanceDisplayCondition = new Cesium.DistanceDisplayConditionGeometryInstanceAttribute(near, far);

  const geometryInstances = new Cesium.GeometryInstance({
    geometry,
    attributes: {color, distanceDisplayCondition}
  });

  return new Cesium.GroundPrimitive({geometryInstances});
}

class FeatureConverter extends olcsFeatureConverter {

  constructor(scene, ngeoLocation) {
    super(scene);
    /**
     * @type {number}
     */
    this.resolutionDistanceFactor = 305 * 0.4; // nonsensical value

    /**
     * @type {number}
     * @private
     */
    this.farDistance_ = 130;

    if (ngeoLocation.hasParam('fardistance')) {
      this.farDistance_ = parseInt(ngeoLocation.getParam('fardistance'), 10);
    }

    // For detour lines size change
    /**
     * @type {BoundingSphere}
     * @private
     */
    this.detourBoundingSphere_ = new Cesium.BoundingSphere();
    /**
     * @type {Cartesian3}
     * @private
     */
    this.detourLinePoint1_ = new Cesium.Cartesian3();
    /**
     * @type {Cartesian3}
     * @private
     */
    this.detourLinePoint2_ = new Cesium.Cartesian3();

    /**
     * @type {Color}
     * @private
     */
    this.dashedLineOddColor_ = new Cesium.Color(0, 0, 0, 0);
  }
  /**
   * @private
   * @param {ol.style.Style|Array<ol.style.Style>|undefined} style
   * @return {Array<ol.style.Style>}
   */
  normalizeStyle_(style) {
    if (!style) {
      return [];
    }
    if (!Array.isArray(style)) {
      return [style];
    }
    return style;
  }

  /**
   * @override
   */
  computePlainStyle(layer, feature, fallbackStyle, resolution) {
    const style3d = layer.get('style3d');
    if (style3d) {
      if (typeof style3d === 'function') {
        return this.normalizeStyle_(style3d(feature, resolution));
      } else {
        return this.normalizeStyle_(/** @type {?} */ (style3d));
      }
    }

    const featureStyle = feature.getStyleFunction();
    let style;
    if (featureStyle) {
      style = featureStyle.call(feature, resolution);
    } else if (fallbackStyle) {
      style = fallbackStyle(feature, resolution);
    }

    style = this.normalizeStyle_(style);
    if (style.length === 0) {
      // The feature must not be displayed
      return [];
    }

    const firstStyle = style[0];
    const stroke = firstStyle.getStroke();
    if (stroke) {
      const color = stroke.getColor();
      if (color[3] === 0.01) {
        // Case of the track style
        console.assert(style[1]);
        return [style[1]];
      }
    }

    return style;
  }


  /**
   * @param {!ol.geom.LineString} lineGeometry
   * @return {Array.<ol.Coordinate>}
   */
  toRegularDots(lineGeometry) {
    const interval = ARROW_3D_INTERVAL_METERS;
    const length = lineGeometry.getLength();

    const dots = new Array(Math.floor(length / interval));
    for (let i = 1; i <= dots.length; i++) {
      dots[i - 1] = lineGeometry.getCoordinateAt(i * interval / length);
    }

    return dots;
  }


  /**
   * @param {ol.layer.Vector|ol.layer.Image} layer
   * @param {!ol.Feature} feature
   * @param {!ol.geom.LineString} geometry
   * @param {!ol.proj.Projection|string} projection
   * @param {!ol.style.Style} style
   * @return {!Cesium.BillboardCollection}
   */
  olLineToCesiumBillboards(layer, feature, geometry, projection, style) {

    console.assert(geometry.getType() == 'LineString');
    const dotsInMapProjection = this.toRegularDots(geometry); // geometry in map projection
    const transformTo4326 = olProjTransformsGet(projection.getCode(), 'EPSG:4326');
    geometry = olGeometryCloneTo4326(geometry, projection);

    const billboards = new Cesium.BillboardCollection({
      scene: this.scene
    });

    const imageStyle = style.getImage();

    const image = imageStyle.getImage(1); // get normal density

    const dots = dotsInMapProjection.map(coordinates => transformTo4326(coordinates));
    const positions = ol4326CoordinateArrayToCsCartesians(dots);

    const heightReference = this.getHeightReference(layer, feature, geometry);
    const nearFarScalar = new Cesium.NearFarScalar(1000, 1.0, 1e6, 0);
    const maxDistance = 155000;
    const distances = [
      new Cesium.DistanceDisplayCondition(0, maxDistance / 50),
      new Cesium.DistanceDisplayCondition(0, maxDistance)
    ];
    let dIndex = distances.length;
    for (let i = 0; i < positions.length; ++i) {
      const position = positions[i];
      console.assert(image instanceof HTMLCanvasElement || image instanceof HTMLImageElement);
      console.assert(position);
      if (dIndex <= 0) {
        dIndex = distances.length;
      }
      const distanceDisplayCondition = distances[--dIndex];
      const bbOptions = /** @type {Cesium.optionsBillboardCollectionAdd} */ ({
        image: image,
        heightReference: heightReference,
        scaleByDistance: nearFarScalar,
        distanceDisplayCondition: distanceDisplayCondition,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        position: position
      });
      this.csAddBillboard(billboards, bbOptions, layer, feature, geometry, style);
    }
    return billboards;
  }


  /**
   * Convert an OpenLayers line string geometry to Cesium.
   * @override
   */
  olLineStringGeometryToCesium(layer, feature, olGeometry, projection, olStyle) {
    console.assert(projection);
    const geom = olGeometry;
    const style = olStyle;
    const proj = projection;
    if (olStyle.getImage()) {
      const both = new Cesium.PrimitiveCollection();
      console.assert(geom);
      both.add(this.olLineToCesiumBillboards(layer, feature, geom, proj, style));
      return both;
    } else {
      return super.olLineStringGeometryToCesium(layer, feature, geom, proj, style);
    }
  }

  /**
   * @override
   */
  olStyleToCesium(feature, style, outline) {
    const fill = style.getFill();
    const stroke = style.getStroke();
    if ((outline && !stroke) || (!outline && !fill)) {
      return null;
    }

    let color = outline ? stroke.getColor() : fill.getColor();
    color = convertColorToCesium(color);

    if (outline && stroke.getLineDash()) {
      const extent = mapProjectionExtentToLonLat(feature.getGeometry().getExtent());
      Cesium.Cartesian3.fromDegrees(extent[0], extent[1], 0, Cesium.Ellipsoid.WGS84, this.detourLinePoint1_);
      Cesium.Cartesian3.fromDegrees(extent[2], extent[3], 0, Cesium.Ellipsoid.WGS84, this.detourLinePoint2_);
      Cesium.BoundingSphere.fromCornerPoints(this.detourLinePoint1_, this.detourLinePoint2_, this.detourBoundingSphere_);
      const distance = this.scene.camera.distanceToBoundingSphere(this.detourBoundingSphere_);
      const geom = feature.getGeometry();
      let lineLengths;
      if (geom.getLength) {
        lineLengths = geom.getLength();
      } else {
        lineLengths = geom.getLineStrings().reduce((acc, l) => acc + l.getLength(), 0);
      }

      return Cesium.Material.fromType('Stripe', {
        horizontal: false,
        repeat: lineLengths / (distance / 100),
        evenColor: color,
        oddColor: this.dashedLineOddColor_
      });
    } else {
      return Cesium.Material.fromType('Color', {
        color
      });
    }

  }


  /**
   * @override
   */
  olPointGeometryToCesium(layer, feature, olGeometry, projection, style, billboards,
    opt_newBillboardCallback) {
    const customPoint = olGeometry.get('olcsCustomPoint');
    if (customPoint) {
      const primitives = new Cesium.PrimitiveCollection();

      const cesiumColor = convertColorToCesium(customPoint['color']);
      const polygonsLatLng = customPoint['polygonsLatLng'];

      polygonsLatLng.forEach((polygonLatLng, level) => {
        primitives.add(makePolygonPrimitive_(polygonLatLng, cesiumColor, level));
      });

      return primitives;
    } else {
      if (layer.get('kind') === 'geojson_slowup_layer' || feature.get('subtype') === 'Direction_Arrow' || feature.get('subtype') === 'Climbing_Descent') {
        // slowup case
        return; // skip POIs with rotations, we don't know how to handle them and vector tile pois
      }
      return super.olPointGeometryToCesium(
        layer, feature, olGeometry, projection, style, billboards, opt_newBillboardCallback
      );
    }
  }


  /**
   * @override
   */
  csAddBillboard(billboards, bbOptions, layer, feature, geometry, style) {
    const minResolution = /** @type {number} */ (feature.get('resolution'));
    const maxDistance = /** @type {number} */ (feature.get('maxDistance') || layer.get('maxDistance'));
    if (maxDistance) {
      bbOptions.distanceDisplayCondition = new Cesium.DistanceDisplayCondition (0, maxDistance);
    } else if (minResolution !== undefined && minResolution > 0) {
      const minDistance = calcDistanceForResolution(minResolution, Math.PI / 4, this.scene, proj21781);
      const visibilityDistances = new Cesium.DistanceDisplayCondition(0, minDistance);
      bbOptions.distanceDisplayCondition = visibilityDistances;
    }

    if (feature.getId() === QUERY_HIGHLIGHT_ID) {
      // Shift the feature 50m closer to the camera so that it appears 'above'.
      // The chosen value should be less than the minimum distance to the ground.
      bbOptions.eyeOffset = new Cesium.Cartesian3(0, 0, 50);
    } else if (String(feature.getId()).includes(QUERY_HIGHLIGHT_ID)) {
      bbOptions.eyeOffset = new Cesium.Cartesian3(0, 0, 0);
      bbOptions.scaleByDistance = new Cesium.NearFarScalar(1, 1.0, this.farDistance_ * 1000, 0.0);
    } else {
      // Reduce all points with distance (3D POIs, profile cross and maybe others).
      // Far distance converted to meters.
      bbOptions.scaleByDistance = new Cesium.NearFarScalar(1, 1.0, this.farDistance_ * 1000, 0.0);
      // with negative eyeOffset billboards still visible at close distances and don't go below terrain
      bbOptions.eyeOffset = new Cesium.Cartesian3(0, 0, -50);
    }

    return super.csAddBillboard(billboards, bbOptions, layer, feature, geometry, style);
  }
}

export default FeatureConverter;
